import React, { useEffect, useState } from 'react';
import { Banner, Button, Flex, FlexItem } from '@patternfly/react-core';
import { InfoCircleIcon, SyncAltIcon } from '@patternfly/react-icons';
import { useNewVersionAvailable } from '../../hooks/useNewVersionAvailable';
import { RUNNING_VERSION } from '../../utils/appVersion';

/** « Later » hides the banner for this long (in memory only: a reload is exactly what it suggests). */
export const SNOOZE_MS = 60 * 60 * 1000;

/**
 * Reload onto the new release. The bundles have content-hashed names and index.html is served no-cache,
 * so a plain reload fetches the new release; Cache Storage (unused by this app today) is cleared too,
 * defensively, where the browser offers it.
 */
export async function reloadToNewVersion(): Promise<void> {
  try {
    if (typeof window.caches !== 'undefined') {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((key) => window.caches.delete(key)));
    }
  } catch {
    // Cache Storage unavailable (plain HTTP, some private modes): nothing to clear.
  }
  window.location.reload();
}

/**
 * « A new version of the Manager is available » — shown once `/version.json` names a release other than the
 * one running (see useNewVersionAvailable), so a tab left open across a deploy does not keep running old
 * code against a newer backend. Reload now, or Later (back in an hour).
 */
export const NewVersionBanner: React.FC<{ runningVersion?: string; onReload?: () => void }> = ({
  runningVersion = RUNNING_VERSION,
  onReload = () => void reloadToNewVersion(),
}) => {
  const available = useNewVersionAvailable(runningVersion);
  const [snoozedUntil, setSnoozedUntil] = useState<number | null>(null);

  useEffect(() => {
    if (snoozedUntil === null) {
      return undefined;
    }
    const timer = window.setTimeout(() => setSnoozedUntil(null), Math.max(0, snoozedUntil - Date.now()));
    return () => window.clearTimeout(timer);
  }, [snoozedUntil]);

  if (available === null || snoozedUntil !== null) {
    return null;
  }

  return (
    // PF's guideline for « an application upgrade is available »: the blue (light) banner, not a status one.
    <Banner color="blue">
      <Flex
        alignItems={{ default: 'alignItemsCenter' }}
        spaceItems={{ default: 'spaceItemsSm' }}
        flexWrap={{ default: 'wrap' }}
      >
        <FlexItem>
          <InfoCircleIcon aria-hidden />
        </FlexItem>
        <FlexItem>
          A new version of the Manager is available ({available}; you are running {runningVersion}).
        </FlexItem>
        <FlexItem>
          <Button variant="primary" size="sm" icon={<SyncAltIcon />} onClick={onReload}>
            Reload
          </Button>
        </FlexItem>
        <FlexItem>
          <Button variant="secondary" size="sm" onClick={() => setSnoozedUntil(Date.now() + SNOOZE_MS)}>
            Later
          </Button>
        </FlexItem>
      </Flex>
    </Banner>
  );
};

export default NewVersionBanner;
